import React, { useState, useEffect } from "react";
import { getRealEstateParallelAnalysis } from "../../../../../shared/services/apis/tenantAnalytics";
import ReactMarkdown from "react-markdown";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import IframeViewer from "../../../../../shared/ui/urlOpener/IFrameViewer";
import { PiBuildingsBold } from "react-icons/pi";


interface MarkdownResult {
  question: string;
  result: string;
}

interface RealStateComponentProps {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
}

const RealStateComponent: React.FC<RealStateComponentProps> = ({
  tenantId,
  tenantServiceId,
  tenantServiceOptionId,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [data, setData] = useState<MarkdownResult[]>([]);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId || !tenantServiceId || !tenantServiceOptionId) return;

    const fetchTenantData = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await getRealEstateParallelAnalysis(
          tenantId,
          tenantServiceId,
          tenantServiceOptionId
        );
        setData(response);
      } catch (err: any) {
        console.error("Error fetching tenant overview:", err);
        setError("Failed to load real estate analysis. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchTenantData();
  }, [tenantId, tenantServiceId, tenantServiceOptionId]);
  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setIframeUrl(url);
  };

  const renderSkeletons = () =>
    Array(6)
      .fill(0)
      .map((_, index) => (
        <div
          key={index}
          className="bg-white rounded-xl shadow-md p-6 space-y-3 border border-gray-200 animate-pulse"
        >
          <Skeleton height={24} width={200} /> {/* Simulating question title */}
          <Skeleton count={4} height={14} /> {/* Simulating markdown content */}
        </div>
      ));

  const renderDataCards = () =>
    data.map((item, index) => (
      <div
        key={index}
        className="bg-white rounded-lg shadow-md p-6 hover:shadow-xl transition-all border border-gray-200"
      >
        <h3 className="text-lg font-manrope  mb-2 text-primary">
          {item.question}
        </h3>
        <div className="prose max-w-none font-manrope font-light text-gray-700">
          <ReactMarkdown
            components={{
              a: ({ href, children }) => {
                let pageNum: string | null = null;

                try {
                  if (href) {
                    const hash = new URL(href).hash; // e.g. "#page=11"
                    pageNum = hash.includes("=") ? hash.split("=")[1] : null;
                  }
                } catch {
                  pageNum = null;
                }
                if (!pageNum && children) {
                  pageNum = String(children);
                }

                return (
                  <button
                    onClick={(e) => handleSourceClick(e, href || "")}
                    className="text-gray-600 text-xs font-thin cursor-pointer hover:underline"
                  >
                    [{pageNum || "?"}]
                  </button>
                );
              },
            }}
          >
            {item.result}
          </ReactMarkdown>
        </div>
      </div>
    ));

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-4xl">
      <h2 className="text-2xl  flex  items-center font-manrope font-light text-gray-800 mb-4">
          <PiBuildingsBold className="text-xl h-6 w-6 text-gray-800 mr-1" />
  
         Real Estate Project Analysis
      </h2>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex justify-between items-center">
          <span>{error}</span>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-100 text-red-700 px-3 py-1 rounded-md hover:bg-red-200 text-sm"
          >
            Retry
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6">
        {loading ? (
          renderSkeletons()
        ) : data.length > 0 ? (
          renderDataCards()
        ) : (
          <div className="col-span-full text-center text-gray-500 py-10">
            No data available.
          </div>
        )}
      </div>
      {iframeUrl && (
        <IframeViewer url={iframeUrl} onClose={() => setIframeUrl(null)} />
      )}
    </div>
  );
};

export default RealStateComponent;
